import { useForm } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { FunnelFooter } from '../-funnel-components/funnel-footer';
import { FunnelToolbar } from '../-funnel-components/funnel-toolbar';
import { HeadingOption } from '../-funnel-components/headings';
import { ErrorField } from '../../../../components/inputs/error-field';
import { Skeleton } from '../../../../components/skeleton';
import { ToastVariant } from '../../../../components/toast';
import {
  ContentAiFunnelStatus,
  type ContentFunnel,
  contentFunnelSchema,
} from '../../../../entity/api/content-funnel';
import { ForbiddenError } from '../../../../error';
import { convertError } from '../../../../error/convert';
import { addTrackCrumb, trackError } from '../../../../error/tracking';
import { type ErrorAny, mapErrorToTranslation } from '../../../../error/utils';
import { captureException } from '../../../../logger/capture-exception';
import { getQueryClient } from '../../../../sdk/request';
import { PostHogEvents, capturePostHogEvent } from '../../../../services/posthog';
import {
  fetchArticleByIdImperative,
  useArticleFunnelByArticleId,
  useSaveArticleFunnel,
} from '../../../../services/storage';
import { tGlobal, tNamespace } from '../../../../services/translation';
import { useToast } from '../../../../shared/contexts/toast-context';
import { parseFirstZodError } from '../../../../shared/validation';

const t = tNamespace('app.funnel-title');
const tErrors = tNamespace('errors');

export const Route = createFileRoute('/app/funnel/$id/_layout-funnel/title')({
  beforeLoad: async function (data: { params: { id: string } }) {
    addTrackCrumb({
      category: 'route',
      message: '/app/funnel/$id/_layout-funnel/title',
      level: 'info',
    });
    addTrackCrumb({
      category: 'funnel',
      message: 'entering funnel title',
      level: 'info',
    });
    const { id } = data.params;
    const queryClient = getQueryClient();

    try {
      await fetchArticleByIdImperative(queryClient, id);
    } catch (err) {
      captureException(
        err,
        {
          file: '_layout-funnel.title.tsx',
          method: 'loader',
          infoMessage: 'unable to get article by id',
          contentId: id,
        },
        trackError
      );

      throw err;
    }
  },
  // eslint-disable-next-line no-use-before-define
  component: TitleRoute,
});

function getSelectedTitle(articleFunnel?: ContentFunnel) {
  return {
    selectedTitle: articleFunnel?.selectedTitle ?? '',
  };
}

function createHeadings(headings: string[]) {
  return headings.map((title, index) => ({
    id: `ai-title-${index}`,
    text: title,
  }));
}

function TitleRoute() {
  const { id } = Route.useParams() as unknown as { id: string };

  const navigate = useNavigate();
  const [headings, setHeadings] = useState<{ id: string; text: string }[]>([]);
  const [customHeading, setCustomHeading] = useState<string>('');
  const [errors, setErrors] = useState<string>();
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const addToast = useToast();

  // TanStack Query hooks
  const articleFunnelQuery = useArticleFunnelByArticleId(id);
  const articleFunnel = articleFunnelQuery.data ?? null;
  const saveFunnelMutation = useSaveArticleFunnel();

  const form = useForm<Partial<ContentFunnel>>({
    defaultValues: getSelectedTitle(articleFunnel ?? undefined),
    onSubmit: async function (values) {
      setErrors(undefined);
      const contentId = id;
      if (contentId.length === 0) {
        // TODO : change error message
        setErrors(t('titleIsRequired'));
        return;
      }

      addTrackCrumb({
        category: 'funnel',
        message: `advancing content funnel to ${ContentAiFunnelStatus.ReqGenOutlines}`,
        level: 'info',
      });

      const startNextStepGeneration = articleFunnel?.status === ContentAiFunnelStatus.ReqGenTitles;

      try {
        await saveFunnelMutation.mutateAsync({
          articleId: contentId,
          startNextStepGeneration,
          data: {
            selectedTitle: values.value.selectedTitle ?? '',
          },
        });
        capturePostHogEvent(PostHogEvents.FUNNEL_STEP2_COMPLETED, { contentId });
        await navigate({
          to: `/app/funnel/${id}/outlines`,
        });
      } catch (err) {
        const handledErr = convertError(err);

        // handle forbiddens (which comes from plan features limitations)
        // as such, the original message is a specific enum incoming
        if (handledErr instanceof ForbiddenError) {
          addToast({
            variant: ToastVariant.Error,
            title: tErrors(handledErr.originalMessage),
            id: Date.now(),
          });
          return;
        }

        addToast({
          title: tGlobal('somethingWentWrong'),
          variant: ToastVariant.Error,
          id: Date.now(),
        });

        const errMsg = mapErrorToTranslation(err as ErrorAny);
        setErrors(errMsg);
        captureException(
          err,
          {
            file: 'app/funnel/$id/_layout-funnel.title.tsx',
            method: 'onSubmit',
            infoMessage: 'unable to update content funnel',
          },
          trackError
        );
      }
    },
  });

  function saveTitle(title: string) {
    // Don't save during initial load or if it's equal to the selected title
    if (isInitialLoad || title === articleFunnel?.selectedTitle) {
      return;
    }

    if (articleFunnel == null) {
      setErrors(t('noArticleFunnelFound'));
      return;
    }

    saveFunnelMutation.mutate(
      {
        articleId: id,
        startNextStepGeneration: false,
        data: {
          selectedTitle: title,
        },
      },
      {
        onError: function (err) {
          addToast({
            title: tGlobal('somethingWentWrong'),
            variant: ToastVariant.Error,
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'funnel/$id/_layout-funnel.title.tsx',
              method: 'saveTitle',
              infoMessage: 'unable to save title',
              id,
            },
            trackError
          );
        },
      }
    );
  }

  function saveCustomizedAiTitles(titles: { id: string; text: string }[], selectedTitle: string) {
    saveFunnelMutation.mutate(
      {
        articleId: id,
        startNextStepGeneration: false,
        data: {
          customizedAiTitles: titles.map(function (title) {
            return title.text;
          }),
          selectedTitle: selectedTitle,
        },
      },
      {
        onError: function (err) {
          addToast({
            title: tGlobal('somethingWentWrong'),
            variant: ToastVariant.Error,
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'funnel/$id/_layout-funnel.title.tsx',
              method: 'saveCustomizedAiTitles',
              infoMessage: 'unable to save customizedAiTitles',
            },
            trackError
          );
        },
      }
    );
  }

  // Handle side effects when articleFunnel changes
  useEffect(
    function () {
      const aiTitles = articleFunnel?.aiTitles;
      const articleFunnelToBe = articleFunnel;
      if (articleFunnelToBe == null || aiTitles == null) {
        setErrors(t('noArticleFunnelFound'));
        return;
      }

      const customizedAiTitles = articleFunnelToBe.customizedAiTitles ?? [];

      const aiHeadings = customizedAiTitles.length > 0 ? customizedAiTitles : aiTitles;
      setHeadings(createHeadings(aiHeadings));

      // Only run initialization logic on initial load
      if (!isInitialLoad) {
        return;
      }
      // Always set initial load to false after processing the data
      setIsInitialLoad(false);

      if (articleFunnelToBe.selectedTitle === '') {
        return;
      }

      // Check if it's a custom title
      const isAiTitle = [...customizedAiTitles, ...aiTitles].includes(
        articleFunnelToBe.selectedTitle
      );
      if (!isAiTitle) {
        setCustomHeading(articleFunnelToBe.selectedTitle);
      }
      // Reset the form with the stored selected title
      form.reset(getSelectedTitle(articleFunnelToBe));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [articleFunnel, isInitialLoad]
  );

  useEffect(
    function () {
      if (articleFunnel?.status === ContentAiFunnelStatus.Brief) {
        navigate({
          to: `/app/blogs`,
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'funnel/$id/outlines',
              method: 'useEffect',
              infoMessage: 'unable to navigate to /app/funnel/${id}/title',
              id,
            },
            trackError
          );
        });
      }
    },
    [articleFunnel, id, navigate]
  );

  if (
    articleFunnel == null ||
    (articleFunnel.status !== ContentAiFunnelStatus.ReqGenTitles &&
      articleFunnel.status !== ContentAiFunnelStatus.ReqGenOutlines) ||
    headings.length === 0
  ) {
    return (
      <div className="relative flex-1 overflow-hidden pb-14">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <FunnelToolbar
        contentFunnel={articleFunnel}
        isLoading={saveFunnelMutation.isPending}
        isDisabled={form.state.values.selectedTitle === ''}
      />
      <div className="relative h-full flex-1 overflow-auto pb-14">
        <div className="flex justify-center px-10 pb-40">
          <div className="mt-6 flex w-editor flex-col px-4">
            <p className="font-display-3">{t('selectTitle')}</p>
            <p className="font-body-2-r text-branding-gray">{t('h1Description')}</p>
            <ErrorField error={errors} />
            <form>
              <form.Field
                name="selectedTitle"
                validators={{
                  onBlur: function (data) {
                    return parseFirstZodError(contentFunnelSchema.shape.selectedTitle, data.value);
                  },
                }}
                // eslint-disable-next-line react/no-children-prop
                children={function (field) {
                  return (
                    <div className="mt-6 flex flex-col gap-6">
                      {headings.map((heading) => (
                        <HeadingOption
                          key={heading.id}
                          text={heading.text}
                          isSelected={field.state.value === heading.text}
                          onClick={function () {
                            setErrors(undefined);
                            field.handleChange(heading.text);
                            // Only save if not the initial load
                            if (!isInitialLoad) {
                              void saveTitle(heading.text);
                            }
                          }}
                          onChange={function (text) {
                            setErrors(undefined);
                            field.handleChange(text);
                            const newHeadings = headings.map(function (h) {
                              if (h.id === heading.id) {
                                return { ...h, text };
                              }
                              return h;
                            });
                            setHeadings(newHeadings);
                            // Only save if not the initial load
                            if (!isInitialLoad) {
                              void saveCustomizedAiTitles(newHeadings, text);
                            }
                          }}
                        />
                      ))}
                      <HeadingOption
                        key="custom"
                        text={customHeading}
                        isCustom={customHeading === ''}
                        isSelected={field.state.value === customHeading && customHeading !== ''}
                        onClick={function () {
                          setErrors(undefined);
                          field.handleChange(customHeading);
                          // Only save if not the initial load
                          if (!isInitialLoad) {
                            void saveTitle(customHeading);
                          }
                        }}
                        onChange={function (text) {
                          setErrors(undefined);
                          setCustomHeading(text);
                          field.handleChange(text);
                          void saveTitle(text);
                        }}
                      />
                    </div>
                  );
                }}
              />
            </form>
          </div>
        </div>
      </div>
      <FunnelFooter
        contentFunnel={articleFunnel}
        isLoading={saveFunnelMutation.isPending}
        isDisabled={form.state.values.selectedTitle === ''}
        onPrevious={function () {
          void navigate({ to: `/app/funnel/${id}/customize-content`, params: { id } });
        }}
        onSubmit={function () {
          void form.handleSubmit();
        }}
      />
    </div>
  );
}
